import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoriesAPI } from '../services/api';
import './EnhancedSearch.css';

const EnhancedSearch = ({ onClose, isOpen }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoriesAPI.getFeaturedCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim() || selectedCategory) {
      const query = searchQuery.trim();
      const searchParams = new URLSearchParams();
      
      // Always redirect to talent search page (this is the "Hunt Talent" search)
      if (query) {
        searchParams.append('search', query);
      }
      
      if (selectedCategory) {
        searchParams.append('category', selectedCategory.slug);
      }

      navigate(`/talent-search?${searchParams.toString()}`);
      onClose();
    }
  };

  const handleCategorySearch = (category) => {
    setSelectedCategory(category);
    setSearchQuery(category.name);
    setShowCategoryDropdown(false);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.trim()) {
      const filtered = categories.filter(category =>
        category.name.toLowerCase().includes(value.toLowerCase()) ||
        category.description?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCategories(filtered);
      setShowCategoryDropdown(true);
    } else {
      setFilteredCategories([]);
      setShowCategoryDropdown(false);
      setSelectedCategory(null);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedCategory(null);
    setFilteredCategories([]);
    setShowCategoryDropdown(false);
  };

  const popularSearches = [
    'React Developer',
    'Logo Designer',
    'Social Media Manager',
    'Mobile App Developer',
    'WordPress Expert',
    'Content Writer',
    'SEO Specialist',
    'UI/UX Designer'
  ];

  const quickCategorySearches = [
    { name: 'Programming & Tech', icon: 'bi bi-code-slash', slug: 'programming-tech' },
    { name: 'Design', icon: 'bi bi-palette', slug: 'design' },
    { name: 'Social Media Marketing', icon: 'bi bi-share', slug: 'social-media-marketing' },
    { name: 'WordPress', icon: 'bi bi-wordpress', slug: 'wordpress' },
    { name: 'Mobile Application', icon: 'bi bi-phone', slug: 'mobile-application' }
  ];

  if (!isOpen) return null;

  return (
    <div className="enhanced-search-overlay">
      <div className="enhanced-search-container">
        <div className="enhanced-search-header">
          <h2>Browse Talent Categories</h2>
          <p>Discover skilled professionals across different categories and skills</p>
          <button 
            className="search-close-btn"
            onClick={onClose}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>

        <form onSubmit={handleSearch} className="enhanced-search-form">
          <div className="search-input-group">
            <div className="search-field">
              <span className="search-icon">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                placeholder="Search for talent, skills, or categories..."
                value={searchQuery}
                onChange={handleInputChange}
                className="search-input"
                autoFocus
              />
              {searchQuery && (
                <button 
                  type="button" 
                  className="clear-btn"
                  onClick={clearSearch}
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
            
            {/* Category Dropdown */}
            {showCategoryDropdown && filteredCategories.length > 0 && (
              <div className="category-dropdown">
                {filteredCategories.slice(0, 5).map((category) => (
                  <div
                    key={category.id}
                    className="category-dropdown-item"
                    onClick={() => handleCategorySearch(category)}
                  >
                    <div className="category-item-icon">
                      <i className={category.icon || 'bi bi-tag'}></i>
                    </div>
                    <div className="category-item-details">
                      <span className="category-item-name">{category.name}</span>
                      {category.description && (
                        <span className="category-item-desc">{category.description}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}


            <button type="submit" className="search-btn">
              <i className="bi bi-search"></i>
              Hunt Talent
            </button>
          </div>
        </form>

        <div className="search-suggestions">
          <div className="suggestion-section">
            <h3>Popular Talent Categories</h3>
            <div className="quick-categories">
              {quickCategorySearches.map((category) => (
                <button
                  key={category.slug}
                  className="quick-category-btn"
                  onClick={() => handleCategorySearch(category)}
                >
                  <i className={category.icon}></i>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="suggestion-section">
            <h3>Popular Skills & Services</h3>
            <div className="popular-searches">
              {popularSearches.map((search, index) => (
                <button
                  key={index}
                  className="popular-search-btn"
                  onClick={() => {
                    setSearchQuery(search);
                    setShowCategoryDropdown(false);
                  }}
                >
                  {search}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedSearch;
